import {
    DateTimeUtils,
    DAY_DATE_FORMAT,
    DAY_SHORT_DATE_FORMAT,
    IRestaurantShift,
    ListItem,
    Pill,
    SERVER_DATE_FORMAT,
    TIME_FORMAT,
    TLanguageKey,
    TPillTone,
} from '@localina/core';
import { NightLifeIcon } from '@localina/icons';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
    specialOpeningHour: IRestaurantShift;
    onClick: (specialOpeningHour: IRestaurantShift) => void;
}

export const SpecialOpeningHourListItem: React.FC<IProps> = (props) => {
    const { t, i18n } = useTranslation();

    const title = props.specialOpeningHour.name[i18n.language as TLanguageKey];

    const handleClick = () => {
        props.onClick(props.specialOpeningHour);
    };

    const dates = props.specialOpeningHour.shiftType === 'soh' ? props.specialOpeningHour.dates : [];

    const sortedDates = useMemo(() => [...dates].sort(), [JSON.stringify(dates)]);
    const isDateRange = useMemo(
        () =>
            sortedDates.length > 1 &&
            DateTimeUtils.areDatesConsecutive(sortedDates.map((v) => DateTime.fromFormat(v, SERVER_DATE_FORMAT))),
        [sortedDates],
    );

    const formatDate = (date: string) =>
        DateTime.fromFormat(date, SERVER_DATE_FORMAT).toFormat(DAY_DATE_FORMAT, { locale: i18n.language });

    const weekdays = new Set(dates.map((date) => DateTime.fromFormat(date, SERVER_DATE_FORMAT).weekday));
    const sharedWeekday =
        weekdays.size === 1
            ? DateTime.fromFormat(dates[0], SERVER_DATE_FORMAT).toFormat('ccc', { locale: i18n.language })
            : undefined;

    const timeFrom = DateTime.fromFormat(props.specialOpeningHour.from, TIME_FORMAT).toFormat(TIME_FORMAT);
    const timeTo = DateTime.fromFormat(props.specialOpeningHour.to, TIME_FORMAT).toFormat(TIME_FORMAT);
    const time = `${timeFrom} – ${timeTo}`;

    let when: string;
    if (sortedDates.length === 1) {
        when = `${formatDate(sortedDates[0])} · ${time}`;
    } else if (isDateRange) {
        when = `${formatDate(sortedDates[0])} – ${formatDate(sortedDates[sortedDates.length - 1])} · ${time}`;
    } else {
        when = [
            t('availability.datesCount', { count: sortedDates.length }),
            [sharedWeekday, time].filter(Boolean).join(' '),
        ].join(' · ');
    }

    const now = DateTime.now();
    const formatSummaryDate = (date: string) => {
        const value = DateTime.fromFormat(date, SERVER_DATE_FORMAT);
        return value.toFormat(value.year === now.year ? DAY_SHORT_DATE_FORMAT : DAY_DATE_FORMAT, {
            locale: i18n.language,
        });
    };

    const today = now.toFormat(SERVER_DATE_FORMAT);
    const nextDate = sortedDates.find((date) => date >= today);
    const dateSummary =
        sortedDates.length > 1 && !isDateRange
            ? [
                  { key: 'first', label: t('availability.firstDate'), date: sortedDates[0] },
                  { key: 'next', label: t('availability.nextDate'), date: nextDate },
                  { key: 'last', label: t('availability.lastDate'), date: sortedDates[sortedDates.length - 1] },
              ]
            : undefined;

    let status: { label: string; tone: TPillTone; extraClassName?: string } | undefined;
    if (sortedDates.length > 0) {
        if (sortedDates[sortedDates.length - 1] < today) {
            status = { label: t('availability.expired'), tone: 'neutral', extraClassName: 'is-off' };
        } else if (sortedDates[0] > today) {
            status = { label: t('availability.upcoming'), tone: 'neutral' };
        } else {
            status = { label: t('availability.ongoing'), tone: 'teal' };
        }
    }

    return props.specialOpeningHour.shiftType !== 'soh' ? null : (
        <ListItem
            classNames={`shift-listitem soh-listitem${dateSummary ? ' has-date-summary' : ''}`}
            leftIcon={
                <span className="shift-listitem__icon">
                    <NightLifeIcon />
                </span>
            }
            title={
                <div className="shift-listitem__row">
                    <div className="shift-listitem__main">
                        <span className="shift-listitem__name">{title}</span>
                        <span className="shift-listitem__when" title={when}>
                            {when}
                        </span>
                    </div>
                    {dateSummary && (
                        <dl className="soh-date-summary">
                            {dateSummary.map((item) => (
                                <div
                                    key={item.key}
                                    className={`soh-date-summary__item soh-date-summary__item--${item.key}${
                                        item.date ? '' : ' is-empty'
                                    }`}
                                >
                                    <dt>{item.label}</dt>
                                    <dd title={item.date ? formatDate(item.date) : undefined}>
                                        {item.date ? formatSummaryDate(item.date) : '–'}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    )}
                    {status && (
                        <Pill
                            tone={status.tone}
                            label={status.label}
                            extraClassName={`shift-listitem__status${
                                status.extraClassName ? ` ${status.extraClassName}` : ''
                            }`}
                        />
                    )}
                    <Pill
                        tone={props.specialOpeningHour.enabled ? 'good' : 'neutral'}
                        label={props.specialOpeningHour.enabled ? t('availability.active') : t('availability.inactive')}
                    />
                </div>
            }
            onClick={handleClick}
        />
    );
};
